import React, { useState } from "react";
import { BRAND_ASSETS, lockupSlotForWidth } from "./brand-assets";

/**
 * <AssetPlaceholder slot="logo.horizontal.night" width={240} height={60} />
 *
 * Rendered when a brand-asset file is missing on disk. Shows the EXACT
 * expected filename so the founder or whoever is placing the file knows
 * which one to drop in. Never draws an approximation of the mark or the
 * compass or a rose. Founder Msg 71 point 5.
 */
export function AssetPlaceholder({ slot, width, height, className = "", style = {} }) {
  const asset = BRAND_ASSETS[slot];
  return (
    <div
      role="img"
      aria-label={`missing brand asset: ${asset?.filename || slot}`}
      className={`grid place-items-center border-2 border-dashed ${className}`}
      style={{
        width, height,
        borderColor: "#FFD60A",
        background: "rgba(255, 214, 10, 0.08)",
        color: "#FFD60A",
        fontFamily: "monospace",
        fontSize: 11,
        lineHeight: 1.3,
        padding: 8,
        textAlign: "center",
        wordBreak: "break-all",
        ...style,
      }}
      data-testid={`asset-placeholder-${slot}`}
    >
      <div>
        <div style={{ opacity: 0.72, marginBottom: 4 }}>missing brand file</div>
        <div style={{ fontWeight: 700 }}>{asset?.filename || slot}</div>
      </div>
    </div>
  );
}

/**
 * <BrandAsset slot="logo.mark" width={64} height={64} alt="" />
 *
 * Uses the manifest path. If the image 404s at runtime, swap in an
 * <AssetPlaceholder /> for the same slot. Never fake it.
 */
export function BrandAsset({ slot, width, height, alt = "", className = "", style = {}, testId }) {
  const asset = BRAND_ASSETS[slot];
  const [broken, setBroken] = useState(false);
  if (!asset || broken) {
    return <AssetPlaceholder slot={slot} width={width} height={height} className={className} style={style} />;
  }
  return (
    <img
      src={asset.path}
      alt={alt}
      width={width}
      height={height}
      className={className}
      style={style}
      data-testid={testId || `brand-${slot}`}
      onError={() => setBroken(true)}
    />
  );
}

/**
 * <LockupLogo width={200} ground="night" />
 *
 * The lockup selection is LOGIC, not a file choice (founder Msg 71 point 6).
 * Picks horizontal / horizontaltight / mark / favicon based on available width,
 * and the ground picks the white-type or ink-type variant. NEVER composes the
 * wordmark as live text next to the mark.
 */
// WW-139 (Vania 12 Oct · Claude QA 2) · the DELIVERED aspect ratios of
// the brand files, measured with PIL on disk (never guessed):
//   logo_horizontal_fullcolour-*-type_1024.png       → 1024×587  = 1.744:1
//   logo_horizontaltight_fullcolour-*-type_1024.png  → 1024×370  = 2.768:1
//   logo_mark_fullcolour-transparent_*.png           → *×*(.887) = 0.887:1
// Tailwind preflight sets `img { height: auto }` which overrides the
// HTML `height` attribute, so we MUST drive both dimensions via inline
// `style` (inline wins over author CSS).  Keeps the brand "never
// stretched" rule intact — the file renders at its delivered aspect.
const _LOCKUP_ASPECTS = {
  "logo.horizontal.night":      1024 / 587,
  "logo.horizontal.cream":      1024 / 587,
  "logo.horizontaltight.night": 1024 / 370,
  "logo.horizontaltight.cream": 1024 / 370,
  "logo.mark":                  1024 / 1154,
  "logo.mark.512":              512 / 577,
};

export function LockupLogo({ width = 200, ground = "night", className = "", style = {}, testId }) {
  const slot = lockupSlotForWidth(width, ground);
  const aspect = _LOCKUP_ASPECTS[slot] || 1;
  const height = Math.round(width / aspect);
  // Inline style wins over the Tailwind preflight `height:auto` on img,
  // so the rendered box matches the HTML attributes exactly.
  const sized = { width, height, ...style };
  return (
    <BrandAsset
      slot={slot}
      width={width}
      height={height}
      alt=""
      className={className}
      style={sized}
      testId={testId || "lockup-logo"}
    />
  );
}
